import { app } from 'application';
import { observer } from 'mobx-react-lite';
import { useEffect, useState, useCallback } from 'react';
import { PageContainer, SearchInput, Pagination } from 'UI/elements';
import styles from './UserManagementPage.module.scss';

type TabId = 'metrics' | 'users';

const METRIC_LABELS: Record<string, string> = {
  total_users: 'Total Users',
  admin_users: 'Admins',
  creative_users: 'Creatives',
  temp_users: 'Temp Users',
  instagram_linked_users: 'Instagram Linked',
  profile_link_clicks: 'Profile Link Clicks',
};

function UserManagementPage(): React.ReactElement {
  const [activeTab, setActiveTab] = useState<TabId>('metrics');

  const store = app().adminUsersStore;
  const metrics = store.metrics;
  const users = store.users;
  const isLoading = store.isLoading;

  const loadUsers = useCallback(
    async (params?: { page?: number; search?: string }) => {
      await store.fetchUsers({
        page: params?.page ?? 1,
        search: params?.search !== undefined ? params.search : undefined,
        role: 'creative',
        per_page: 15,
      });
    },
    [store]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = useCallback(
    async (query: string) => {
      store.setSearch(query);
      await loadUsers({ page: 1, search: query });
    },
    [store, loadUsers]
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      if (page < 1 || page > store.lastPage || page === store.currentPage) return;
      if (page === 1) await loadUsers({ page: 1 });
      else if (page === store.lastPage) await loadUsers({ page: store.lastPage });
      else await loadUsers({ page });
    },
    [store.currentPage, store.lastPage, loadUsers]
  );

  return (
    <PageContainer
      searchComponent={
        activeTab === 'users' ? (
          <SearchInput placeholder="Search users..." onSearch={handleSearch} />
        ) : undefined
      }
    >
      <div className={styles.wrapper}>
        <div className={styles.tabs}>
          <button
            type="button"
            className={activeTab === 'metrics' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('metrics')}
          >
            Metrics
          </button>
          <button
            type="button"
            className={activeTab === 'users' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
        </div>

        {activeTab === 'metrics' && (
          <div className={styles.tabPanel}>
            {isLoading && !metrics ? (
              <div className={styles.loading}>Loading metrics...</div>
            ) : metrics ? (
              <div className={styles.metricsGrid}>
                {(Object.keys(METRIC_LABELS) as Array<keyof typeof metrics>).map(key => (
                  <div key={key} className={styles.metricCard}>
                    <span className={styles.metricLabel}>{METRIC_LABELS[key]}</span>
                    <span className={styles.metricValue}>{metrics[key] ?? 0}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.empty}>No metrics available.</div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className={styles.tabPanel}>
            {isLoading && !users.length ? (
              <div className={styles.loading}>Loading users...</div>
            ) : (
              <>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.headerCell}>User</th>
                        <th className={styles.headerCell}>Email</th>
                        <th className={styles.headerCell}>Roles</th>
                        <th className={styles.headerCell}>Posts</th>
                        <th className={styles.headerCell}>Categories</th>
                        <th className={styles.headerCell}>Instagram Clicks</th>
                        <th className={styles.headerCell}>Website Clicks</th>
                        <th className={styles.headerCell}>Temp</th>
                        <th className={styles.headerCell}>Instagram</th>
                        <th className={styles.headerCell}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id}>
                          <td className={styles.tableCell}>
                            <div className={styles.userCell}>
                              {user.profile_image ? (
                                <img src={user.profile_image} alt="" className={styles.avatar} />
                              ) : (
                                <div className={styles.avatarPlaceholder} />
                              )}
                              <div>
                                <div className={styles.userName}>{user.name}</div>
                                <div className={styles.userUsername}>@{user.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className={styles.tableCell}>{user.email}</td>
                          <td className={styles.tableCell}>{user.roles?.join(', ') ?? '—'}</td>
                          <td className={styles.tableCell}>{user.posts_count}</td>
                          <td className={styles.tableCell}>{user.categories_count}</td>
                          <td className={styles.tableCell}>{user.instagram_link_clicks_count}</td>
                          <td className={styles.tableCell}>{user.website_link_clicks_count}</td>
                          <td className={styles.tableCell}>{user.is_temp ? 'Yes' : 'No'}</td>
                          <td className={styles.tableCell}>
                            {user.instagram_linked ? 'Yes' : 'No'}
                          </td>
                          <td className={styles.tableCell}>
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {store.lastPage > 1 && (
                  <div className={styles.paginationWrapper}>
                    <Pagination
                      currentPage={store.currentPage}
                      totalPages={store.lastPage}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
                {!isLoading && users.length === 0 && (
                  <div className={styles.empty}>
                    {users.length === 0 ? 'No users found.' : 'No users in this date range.'}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </PageContainer>
  );
}

const UserManagementPageWithObserver = observer(UserManagementPage);

export { UserManagementPageWithObserver as UserManagementPage };
